import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { preserveParcelMetadata } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { Preset } from "@redotech/redo-fulfillment-parcel-common/presets";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { useState } from "react";
import { getCheapestMatchingRate } from "../common/util";
import { logPresetShortcut } from "../utils/shortcut-logging";

export const usePresetSelection = (
  fulfillmentGroups: readonly FulfillmentGroupData[],
  updateRows: (updates: FulfillmentGroupUpdate[]) => void,
) => {
  const [selectedPreset, setSelectedPreset] = useState<Preset | undefined>(
    undefined,
  );

  const onPresetSelect = useHandler((preset: Preset) => {
    const updates: FulfillmentGroupUpdate[] = [];
    setSelectedPreset(preset);

    // Log preset shortcut usage if it has a hotkey
    if (preset.hotkey) {
      logPresetShortcut(
        preset._id,
        preset.hotkey,
        preset.name || "Unnamed preset",
        {
          carrier: preset.carrier,
          service: preset.service,
          parcel: preset.parcel?.name,
          totalWeightInOunces: preset.totalWeightInOunces,
          fulfillmentGroupCount: fulfillmentGroups.length,
        },
      );
    }

    for (const group of fulfillmentGroups) {
      const update: FulfillmentGroupUpdate = { fulfillmentGroupId: group._id };
      if (preset.service && preset.carrier) {
        const cheapestMatchingRate = getCheapestMatchingRate({
          service: preset.service,
          carrier: preset.carrier,
          group,
        });

        update.selectedShippingRate = cheapestMatchingRate
          ? {
              carrier: cheapestMatchingRate.carrier,
              service: cheapestMatchingRate.service,
            }
          : null;
      }
      const prevParcel = group.selectedParcels?.[0];
      const newParcel = preset.parcel ?? prevParcel;
      const newWeight = preset.totalWeightInOunces
        ? { value: preset.totalWeightInOunces, unit: WeightUnit.Oz }
        : prevParcel?.parcelAndContentWeight;

      if (newParcel) {
        update.selectedParcels = [
          preserveParcelMetadata(
            { ...newParcel, parcelAndContentWeight: newWeight },
            prevParcel,
          ),
        ];
      }
      updates.push(update);
    }
    updateRows(updates);
  });

  return { selectedPreset, setSelectedPreset, onPresetSelect };
};
